/* eslint-disable */
import {Injectable} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {Order, PagingRequest, Services} from '../../util/services';
import {List, Map as ImmutableMap, Set} from 'immutable';
import {TaskStatisticsResource, TaskStatisticsResourceService} from './task-statistics-resource.service';
import {OffsetDateTime} from "../../util/dates";
import {DownloadedFile} from "../../util/downloaded-files";

/* eslint-enable */

@Injectable()
export class TaskStatisticsService implements TaskStatistics.Service {

  query(request: TaskStatistics.QueryRequest): Observable<List<TaskStatistics.TaskStatistic>> {
    return Observable.create((observer: Observer<List<TaskStatistics.TaskStatistic>>) => {
      const resourceRequest: TaskStatisticsResource.QueryRequest = {
        task_id: Services.createIdParameter(request.taskId),
        user_id: Services.createIdParameter(request.userId),
        from_date: Services.offsetDateTimeToString(request.fromDate),
        to_date: Services.offsetDateTimeToString(request.toDate),
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.order),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      };
      return this.resourceService.query(resourceRequest).subscribe(
        (result: TaskStatisticsResource.TaskStatistic[]) => {
          observer.next(
            this.toPublicList(result)
          );
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  exportXlsx(request: TaskStatistics.QueryRequest): Observable<DownloadedFile> {
    const resourceRequest: TaskStatisticsResource.QueryRequest = {
      task_id: Services.createIdParameter(request.taskId),
      user_id: Services.createIdParameter(request.userId),
      from_date: Services.offsetDateTimeToString(request.fromDate),
      to_date: Services.offsetDateTimeToString(request.toDate)
    };
    return this.resourceService.exportXlsx(resourceRequest);
  }

  get(request: TaskStatistics.GetRequest): Observable<TaskStatistics.TaskStatistic> {
    return Observable.create((observer: Observer<TaskStatistics.TaskStatistic>) => {
      const getRequest: TaskStatisticsResource.QueryRequest = {
        task_id: '' + request.taskId,
        user_id: '' + request.userId,
        from_date: Services.offsetDateTimeToString(request.fromDate),
        to_date: Services.offsetDateTimeToString(request.toDate),
      };
      return this.resourceService.query(getRequest).subscribe(
        (result: TaskStatisticsResource.TaskStatistic[]) => {
          observer.next({
            taskId: result[0].task_id,
            id: result[0].id,
            userId: result[0].user_id,
            createCount: result[0].create_count,
            startCount: result[0].start_count,
            openCount: result[0].open_count,
            recallCount: result[0].recall_count,
            rejectCount: result[0].reject_count,
            finishCount: result[0].finish_count,
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  getSimpleStat(request: { helpdesk?: boolean }): Observable<TaskStatistics.SimpleStat> {
    return Observable.create((observer: Observer<TaskStatistics.SimpleStat>) => {
      return this.resourceService.getSimpleStat({
        helpdesk_enabled: request.helpdesk
      }).subscribe(
        (result: TaskStatisticsResource.SimpleStat) => {
          observer.next({
            stateStat: new Map(Object.entries(result.state_stat)),
            overdueCount: result.overdue_count
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  getHelpdeskStat(): Observable<ImmutableMap<string, number>> {
    return Observable.create((observer: Observer<ImmutableMap<string, number>>) => {
      return this.resourceService.getHelpdeskStat().subscribe(
        (result: ImmutableMap<string, number>) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  constructor(private resourceService: TaskStatisticsResourceService) {
  }

  private toPublicList(resourceList: TaskStatisticsResource.TaskStatistic[]): List<TaskStatistics.TaskStatistic> {
    return List.of(...resourceList.map((r) => this.toPublic(r)));
  }

  private toPublic(r: TaskStatisticsResource.TaskStatistic): TaskStatistics.TaskStatistic {
    return {
      id: r.id,
      taskId: r.task_id,
      userId: r.user_id,
      createCount: r.create_count,
      startCount: r.start_count,
      openCount: r.open_count,
      recallCount: r.recall_count,
      rejectCount: r.reject_count,
      finishCount: r.finish_count
    };
  }

}

export namespace TaskStatistics {

  export interface Service {

    query(request: QueryRequest): Observable<List<TaskStatistic>>;

  }

  export interface QueryRequest {
    taskId?: Set<number>;
    userId?: Set<number>;
    fromDate?: OffsetDateTime;
    toDate?: OffsetDateTime;
    order?: Set<Order<OrderField>>;
    paging?: PagingRequest;
  }

  export interface GetRequest {
    by: string;
    taskId: number;
    userId: number;
    fromDate?: OffsetDateTime;
    toDate?: OffsetDateTime;
  }

  export interface TaskStatistic {
    id: string;
    taskId: number;
    userId: number;
    createCount: number;
    startCount: number;
    openCount: number;
    recallCount: number;
    rejectCount: number;
    finishCount: number;
  }

  export interface SimpleStat {
    stateStat: Map<string, number>;
    overdueCount: number;
  }

  export enum OrderField {
    TASK_ID,
    USER_ID,
    TASK_NAME,
    USER_PERSON_NAME,
    CREATE_COUNT,
    OPEN_COUNT,
    START_COUNT,
    RECALL_COUNT,
    REJECT_COUNT,
    FINISH_COUNT
  }

}

class Keys {

  private static readonly TASK_ID = 'task_id';
  private static readonly USER_ID = 'user_profile_id';
  private static readonly TASK_NAME = 'task_name';
  private static readonly USER_PERSON_NAME = 'user_person_name';
  private static readonly CREATE_COUNT = 'create_count';
  private static readonly START_COUNT = 'start_count';
  private static readonly OPEN_COUNT = 'open_count';
  private static readonly RECALL_COUNT = 'recall_count';
  private static readonly REJECT_COUNT = 'reject_count';
  private static readonly FINISH_COUNT = 'finish_count';

  private static readonly orderFieldKeyImmutableMap: ImmutableMap<TaskStatistics.OrderField, string> = ImmutableMap.of(
    TaskStatistics.OrderField.TASK_ID, Keys.TASK_ID,
    TaskStatistics.OrderField.USER_ID, Keys.USER_ID,
    TaskStatistics.OrderField.TASK_NAME, Keys.TASK_NAME,
    TaskStatistics.OrderField.USER_PERSON_NAME, Keys.USER_PERSON_NAME,
    TaskStatistics.OrderField.CREATE_COUNT, Keys.CREATE_COUNT,
    TaskStatistics.OrderField.OPEN_COUNT, Keys.OPEN_COUNT,
    TaskStatistics.OrderField.START_COUNT, Keys.START_COUNT,
    TaskStatistics.OrderField.RECALL_COUNT, Keys.RECALL_COUNT,
    TaskStatistics.OrderField.REJECT_COUNT, Keys.REJECT_COUNT,
    TaskStatistics.OrderField.FINISH_COUNT, Keys.FINISH_COUNT,
  )

  public static toOrderFieldKey(field: TaskStatistics.OrderField): string {
    return Keys.orderFieldKeyImmutableMap.get(field)!;
  }

}
